import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Globe, CreditCard, Wallet, CheckCircle, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import CryptoTicker from '../components/CryptoTicker';
import Footer from '../components/Footer';
import { FadeUp, StaggerContainer, StaggerItem } from '../components/Motion';

/* ---- Standing card (Revolut hero style, portrait) ---- */
const StandingCard = ({ bg, textColor = 'text-white', chip = 'bg-gradient-to-br from-yellow-200 to-yellow-500', idx, lines = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 160 }}
    animate={{ opacity: 1, y: idx * 14 }}
    transition={{ duration: 0.9, delay: 0.35 + idx * 0.15, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: idx * 14 - 18 }}
    className={`relative w-32 sm:w-40 md:w-52 aspect-[0.64/1] rounded-2xl md:rounded-3xl ${bg} shadow-[0_30px_60px_rgb(0,0,0,0.55)]`}
    style={{ transform: 'rotate(-7deg)', zIndex: 20 - idx, marginLeft: idx === 0 ? 0 : '-1.5rem' }}
  >
    {lines && (
      <div className="absolute inset-0 rounded-2xl md:rounded-3xl overflow-hidden opacity-30"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.25) 0px, rgba(0,0,0,0.25) 1px, transparent 1px, transparent 4px)' }}></div>
    )}
    <div className="absolute inset-0 rounded-2xl md:rounded-3xl bg-gradient-to-tr from-transparent via-white/10 to-transparent pointer-events-none"></div>
    <div className={`absolute top-[38%] left-4 md:left-6 w-8 md:w-10 h-6 md:h-8 rounded-md ${chip} opacity-90`}></div>
    <span
      className={`absolute right-3 md:right-5 top-5 md:top-8 font-extrabold tracking-wide ${textColor} text-sm md:text-lg`}
      style={{ fontFamily: 'Manrope', writingMode: 'vertical-rl' }}
    >
      alphaswapp
    </span>
  </motion.div>
);

const CARD_SETS = {
  physical: [
    { bg: 'gradient-pink-violet', chip: 'bg-gradient-to-br from-yellow-200 to-yellow-500' },
    { bg: 'bg-gradient-to-br from-[#AEB4BA] via-[#E4E7EA] to-[#787E85]', textColor: 'text-gray-800', chip: 'bg-gradient-to-br from-gray-100 to-gray-400' },
    { bg: 'bg-gradient-to-br from-[#2A2D31] via-[#191C1F] to-[#0B0D0F]', chip: 'bg-gradient-to-br from-gray-200 to-gray-500' },
    { bg: 'bg-gradient-to-br from-[#C4C9CF] via-[#9BA1A8] to-[#71767C]', textColor: 'text-gray-900', chip: 'bg-gradient-to-br from-gray-100 to-gray-400', lines: true },
  ],
  virtual: [
    { bg: 'gradient-blue', chip: 'bg-gradient-to-br from-yellow-200 to-yellow-500' },
    { bg: 'gradient-pink-violet', chip: 'bg-gradient-to-br from-gray-100 to-gray-400' },
    { bg: 'bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-800', chip: 'bg-gradient-to-br from-yellow-200 to-yellow-500' },
    { bg: 'bg-gradient-to-br from-[#2A2D31] via-[#191C1F] to-[#0B0D0F]', chip: 'bg-gradient-to-br from-gray-200 to-gray-500' },
  ],
};

const VirtualCard = ({ type, gradient }) => (
  <div className={`relative w-full aspect-[1.586/1] rounded-2xl overflow-hidden ${gradient}`}>
    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent pointer-events-none"></div>
    <div className="relative h-full p-6 flex flex-col justify-between text-white">
      <div className="flex items-start justify-between">
        <span className="text-sm font-extrabold tracking-wide" style={{ fontFamily: 'Manrope' }}>alphaswapp</span>
        {type === 'offline' && <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold border border-white/30">Apple Pay</div>}
        {type === 'online' && <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold border border-white/30">Online</div>}
        {type === 'ads' && <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold border border-white/30">Ads</div>}
      </div>
      <div>
        <div className="text-xl font-mono tracking-wider mb-2 opacity-90">•••• •••• •••• ••••</div>
        <div className="flex justify-between items-end">
          <div>
            <div className="text-[10px] opacity-60">VALID THRU</div>
            <div className="text-xs font-mono">••/••</div>
          </div>
          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/200px-Visa_Inc._logo.svg.png" alt="VISA"
            className="h-4 brightness-0 invert opacity-90" />
        </div>
      </div>
    </div>
  </div>
);

const PaymentCards = () => {
  const [cardSet, setCardSet] = useState('physical');

  const cards = [
    {
      id: 'offline',
      name: 'Carte Hors Ligne',
      title: 'Offline-card',
      description: 'Pour les achats dans les cafés, restaurants, magasins et autres services hors ligne.',
      features: ['Compatible Apple Pay', 'Compatible Google Pay', 'Paiements sans contact'],
      gradient: 'gradient-metal',
    },
    {
      id: 'online',
      name: 'Carte En Ligne',
      title: 'Online-card',
      description: 'Pour les achats en ligne dans les boutiques étrangères et services numériques.',
      features: ['Support $ € £', 'Paiements internationaux', 'Non compatible Apple/Google Pay'],
      gradient: 'gradient-blue',
    },
    {
      id: 'ads',
      name: 'Carte Publicité',
      title: 'Ads Card',
      description: 'Pour les plateformes publicitaires (Google Ads, Facebook, Instagram, etc.).',
      features: ['Spécial publicité', 'Multi-plateformes', 'Usage dédié aux ads'],
      gradient: 'gradient-pink-violet',
    }
  ];

  const comparisonData = [
    { label: 'Émission de carte', values: ['25 USDT', '25 USDT', '2.5 USDT'] },
    { label: 'Dépôt minimum', values: ['25 USDT', '25 USDT', '50 USDT'] },
    { label: 'Maintenance', values: ['-', '-', '-'] },
    { label: 'Frais de recharge', values: ['4%', '4%', '1%'] },
    { label: 'Frais de transaction', values: ['0.25 USDT', '0.25 USDT', '4%'] },
    { label: 'Frais annulation', values: ['-', '-', '$0.30'] },
    { label: 'Système de paiement', values: ['VISA', 'VISA', 'VISA'] },
  ];

  return (
    <div className="min-h-screen bg-white">
      <CryptoTicker />
      <Header />

      {/* ===== HERO — Revolut card stand ===== */}
      <section className="relative pt-16 pb-0 px-6 overflow-hidden" data-testid="cards-hero"
        style={{ background: 'radial-gradient(120% 100% at 50% 0%, #26282B 0%, #111214 45%, #050506 100%)' }}>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-white font-extrabold text-4xl md:text-7xl tracking-[-0.03em] leading-[1.02] mb-6" style={{ fontFamily: 'Manrope' }}>
            Gagnez plus grâce à vos dépenses
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="text-white/70 text-base md:text-lg max-w-2xl mx-auto mb-3">
            Émettez instantanément des cartes virtuelles Visa pour vos achats en ligne et hors ligne. Rechargez avec crypto ou fiat.
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
            className="text-white/40 text-sm mb-8">
            Certaines cartes nécessitent un dépôt minimum. Des frais peuvent s'appliquer.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <Link to="/auth" data-testid="cards-hero-cta">
              <button className="btn-pill-white px-8 py-4 text-base">Émettre une carte</button>
            </Link>
          </motion.div>
        </div>

        {/* Card stand / tray */}
        <div className="relative max-w-4xl mx-auto mt-14 h-[340px] sm:h-[420px] md:h-[520px]">
          {/* back panel */}
          <div className="absolute inset-x-0 top-16 bottom-[-60px] rounded-[48px]"
            style={{ background: 'linear-gradient(150deg, #3A3D41 0%, #17191C 55%, #232629 100%)', transform: 'rotate(-3deg)', border: '1px solid rgba(255,255,255,0.08)' }}></div>

          {/* standing cards */}
          <div className="absolute inset-x-0 top-0 flex justify-center items-start" style={{ transform: 'rotate(-3deg)' }}>
            <AnimatePresence mode="wait">
              <motion.div key={cardSet} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-start">
                {CARD_SETS[cardSet].map((c, idx) => (
                  <StandingCard key={`${cardSet}-${idx}`} idx={idx} bg={c.bg} textColor={c.textColor} chip={c.chip} lines={c.lines} />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* front lip */}
          <div className="absolute inset-x-6 bottom-[-30px] h-28 rounded-[36px]"
            style={{ background: 'linear-gradient(180deg, #2E3134 0%, #101214 100%)', transform: 'rotate(-3deg)', borderTop: '1px solid rgba(255,255,255,0.15)', boxShadow: '0 -20px 50px rgb(0 0 0 / 0.5)' }}></div>
        </div>

        {/* Pill toggles — Revolut style */}
        <div className="relative z-10 flex items-center justify-center space-x-3 pt-14 pb-12">
          <button onClick={() => setCardSet('physical')}
            className={`px-6 py-3 rounded-full text-sm font-bold transition-colors ${cardSet === 'physical' ? 'bg-white text-gray-900' : 'border border-white/30 text-white hover:bg-white/10'}`}
            data-testid="toggle-physical-cards">
            Cartes de débit
          </button>
          <button onClick={() => setCardSet('virtual')}
            className={`px-6 py-3 rounded-full text-sm font-bold transition-colors ${cardSet === 'virtual' ? 'bg-white text-gray-900' : 'border border-white/30 text-white hover:bg-white/10'}`}
            data-testid="toggle-virtual-cards">
            Virtuelles
          </button>
        </div>
      </section>

      {/* ===== Cards Grid ===== */}
      <section className="py-24 px-6 bg-[#F8F9FA]" data-testid="cards-grid">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <h2 className="text-gray-900 font-extrabold text-4xl md:text-5xl tracking-[-0.02em] mb-14 max-w-xl" style={{ fontFamily: 'Manrope' }}>
              Trois cartes, trois usages
            </h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cards.map((card) => (
              <StaggerItem key={card.id} className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.05)] hover:-translate-y-1 transition-transform duration-200" data-testid={`card-${card.id}`}>
                <div className="mb-6"><VirtualCard type={card.id} gradient={card.gradient} /></div>
                <h3 className="text-2xl font-extrabold mb-3 text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>{card.name}</h3>
                <p className="text-gray-500 mb-6 text-sm leading-relaxed">{card.description}</p>
                <div className="space-y-2 mb-8">
                  {card.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                <Link to="/auth">
                  <button className="w-full btn-pill-dark py-3.5 flex items-center justify-center space-x-2" data-testid={`issue-card-${card.id}`}>
                    <span>Émettre une carte</span><ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ===== Comparison (CSS grid — no table) ===== */}
      <section className="py-24 px-6 bg-white" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto">
          <FadeUp>
            <h2 className="text-4xl font-extrabold text-center mb-14 text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Comparaison des cartes alphaswapp
            </h2>
          </FadeUp>
          <FadeUp delay={0.1} className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-[0_12px_40px_rgb(0,0,0,0.06)]">
            <div className="grid grid-cols-4 bg-gray-50 border-b border-gray-100">
              <div className="px-6 py-6"></div>
              {cards.map((card) => (
                <div key={card.id} className="px-4 py-6 text-center">
                  <div className={`w-16 h-10 mx-auto mb-3 rounded-lg ${card.gradient}`}></div>
                  <div className="text-sm md:text-base font-extrabold text-gray-900" style={{ fontFamily: 'Manrope' }}>{card.title}</div>
                </div>
              ))}
            </div>
            {comparisonData.map((row, idx) => (
              <div key={idx} className="grid grid-cols-4 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                <div className="px-6 py-4 text-xs md:text-sm font-medium text-gray-500">{row.label}</div>
                {row.values.map((v, i) => (
                  <div key={i} className="px-4 py-4 text-center text-xs md:text-sm font-mono-data font-bold text-gray-900">{v}</div>
                ))}
              </div>
            ))}
            <div className="grid grid-cols-4 bg-gray-50">
              <div className="px-6 py-5"></div>
              {cards.map((card) => (
                <div key={card.id} className="px-4 py-5 text-center">
                  <Link to="/auth"><button className="btn-pill-dark px-6 md:px-8 py-2.5 text-xs md:text-sm">Émettre</button></Link>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ===== Key Features ===== */}
      <section className="py-24 px-6 bg-black" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-14 tracking-tight max-w-xl" style={{ fontFamily: 'Manrope' }}>
              Fonctionnalités clés
            </h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: 'Émission instantanée', desc: 'Carte prête immédiatement après émission, sans documents ni banque' },
              { icon: Globe, title: 'Paiements internationaux', desc: 'Payez pour des services en ligne en $ € £' },
              { icon: CreditCard, title: 'Sélection pays et banque', desc: 'Choisissez le BIN et la GEO pour votre future carte' },
              { icon: Wallet, title: 'Recharge crypto', desc: 'Rechargez instantanément avec USDT (commission 4%)' }
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <StaggerItem key={idx} className="bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-white" strokeWidth={1.8} />
                  </div>
                  <h3 className="text-xl font-extrabold mb-3 text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>{feature.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed">{feature.desc}</p>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24 px-6 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <FadeUp>
            <h2 className="text-4xl md:text-6xl font-extrabold mb-6 text-gray-900 tracking-[-0.02em]" style={{ fontFamily: 'Manrope' }}>
              Payez partout dans le monde
            </h2>
            <p className="text-lg text-gray-500 mb-10">Avec cryptomonnaie ou fiat — Simple, rapide et sécurisé</p>
            <Link to="/auth" data-testid="cards-cta-btn">
              <button className="btn-pill-dark px-10 py-4 text-base inline-flex items-center space-x-2">
                <span>Commencer maintenant</span><ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </FadeUp>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentCards;
